import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  Skeleton,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { useSelector } from "react-redux";
import axios from "axios";
import { format, startOfWeek, endOfWeek, formatDistanceToNow } from "date-fns";

import MetricCard, {
  AnimatedCard,
  IconWrapper,
} from "components/contacts/MetricCard";
import { People, Timeline, Phone, Notifications } from "@mui/icons-material";
import { useHistory } from "react-router-dom";

import DashFilterBar from "./DashFilterBar";
import UpcomingActivitiesPanel from "./UpcomingActivitiesPanel";
import { normalizeCallOutcome } from "components/contacts/callOutcomeOptions";
import CallLogsPanel from "./CallLogsPanel";
import RemindersPanel from "./RemindersPanel";
import ActivityOverviewChart from "./ActivityOverviewChart";
import TopCompaniesPanel from "./TopCompaniesPanel";
import QuickActionsPanel from "./QuickActionsPanel";

import { COLORS } from "./constants";
export { COLORS };

// ─── helpers ───────────────────────────────────────────────────────────────
const getWeekRange = () => {
  const today = new Date();
  return {
    fromDate: format(startOfWeek(today, { weekStartsOn: 1 }), "yyyy-MM-dd"),
    toDate: format(endOfWeek(today, { weekStartsOn: 1 }), "yyyy-MM-dd"),
    dateLabel: `This Week (${format(startOfWeek(today, { weekStartsOn: 1 }), "dd MMM")} – ${format(endOfWeek(today, { weekStartsOn: 1 }), "dd MMM")})`,
  };
};

const METRICS_PAGE_SIZE = 500;
const browserTimeZone = () =>
  encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone || "");

const getListData = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.responseDetails)) return data.responseDetails;
  if (data && typeof data === "object") {
    return Object.values(data).find(Array.isArray) ?? [];
  }
  return [];
};

const getSettledList = (result, debug, label) => {
  if (result.status === "fulfilled") return getListData(result.value.data);
  if (debug) console.warn(`[DashV2] ${label} request failed:`, result.reason);
  return [];
};

const getSettledCount = (result, fallbackLength = 0) => {
  if (result.status !== "fulfilled") return fallbackLength;
  const data = result.value.data;
  return Number(data?.count ?? fallbackLength);
};

const filterCallsByOutcome = (calls, outcome) => {
  if (!outcome || outcome === "all") return calls;
  const selected = normalizeCallOutcome(outcome);
  return calls.filter(
    (call) => normalizeCallOutcome(call.outcome || call.status) === selected,
  );
};

const getContactName = (item) => {
  const contact = typeof item.contact === "object" ? item.contact : null;
  const contactFullName = contact
    ? [contact.first_name, contact.last_name].filter(Boolean).join(" ")
    : "";
  return (
    item.contact_name ||
    contact?.name ||
    contactFullName ||
    item.name ||
    item.phone_number ||
    "Unknown"
  );
};

const getCompanyName = (item) => {
  const contact = typeof item.contact === "object" ? item.contact : null;
  const company = typeof item.company === "object" ? item.company : null;
  return (
    item.company_name ||
    item.contact_company_name ||
    company?.name ||
    contact?.company_name ||
    contact?.company ||
    ""
  );
};

const normalizeCompanyStats = (data) =>
  getListData(data)
    .map((item) => ({
      company_id:
        item.company_id ?? item.company ?? item.id ?? item.company_name,
      company_name: item.company_name || item.name || item.company || "Unknown",
      call_count: Number(
        item.call_count ?? item.count ?? item.total_calls ?? 0,
      ),
    }))
    .filter((item) => item.company_name && item.call_count > 0)
    .sort((a, b) => b.call_count - a.call_count)
    .slice(0, 5);

const buildCompanyStatsFromCalls = (calls) => {
  const statsMap = new Map();

  calls.forEach((call) => {
    const companyName = getCompanyName(call) || getContactName(call);
    if (!companyName || companyName === "Unknown") return;

    const companyId = call.company_id ?? call.company ?? companyName;
    const key = String(companyId || companyName);
    const current = statsMap.get(key) || {
      company_id: companyId,
      company_name: companyName,
      call_count: 0,
    };

    current.call_count += 1;
    statsMap.set(key, current);
  });

  return Array.from(statsMap.values())
    .sort((a, b) => b.call_count - a.call_count)
    .slice(0, 5);
};

export default function DashboardV2() {
  const requestCompanyId = useSelector(
    (state) => state.tokenReducer.topbarCompanyId,
  );
  const { domain, token } = useSelector((s) => s.tokenReducer);

  const history = useHistory();

  const week = getWeekRange();
  // Default to every agent the logged-in user is allowed to see; the backend
  // scopes the result (own data for agents, team for managers, all for admins).
  const [filters, setFilters] = useState({
    userId: "all",
    fromDate: week.fromDate,
    toDate: week.toDate,
    dateLabel: week.dateLabel,
    statusId: "all",
    outcome: "all",
  });

  const [users, setUsers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [mineContactCount, setMineContactCount] = useState(0);
  const [totalContactCount, setTotalContactCount] = useState(0);
  const [metrics, setMetrics] = useState(null);
  const [contactsMap, setContactsMap] = useState({});
  const [companyStats, setCompanyStats] = useState([]);
  const [allReminders, setAllReminders] = useState([]);

  const [loadingInit, setLoadingInit] = useState(true);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [loadingAllReminders, setLoadingAllReminders] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Tab states for linked navigation
  const [activeKpi, setActiveKpi] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [remindersTab, setRemindersTab] = useState(0);

  const metricsAbortRef = useRef(null);

  const fetchInit = useCallback(async () => {
    if (!domain || !token) return;
    try {
      setLoadingInit(true);
      const headers = { Authorization: `token ${token}` };
      const [usersRes, statusesRes, allContactsRes] = await Promise.all([
        axios.get(
          `${domain}crm/crm-members/active/?company_id=${requestCompanyId}`,
          { headers },
        ),
        axios.get(`${domain}crm/contact-status/?page_size=100`, { headers }),
        axios.get(`${domain}crm/contacts/?page_size=100`, { headers }),
      ]);

      setUsers(
        Array.isArray(usersRes.data)
          ? usersRes.data
          : (usersRes.data?.results ?? []),
      );

      const statusesData = statusesRes.data?.results ?? statusesRes.data ?? [];
      setStatuses(Array.isArray(statusesData) ? statusesData : []);

      const allContacts = allContactsRes.data;
      const contacts =
        allContacts?.results ?? (Array.isArray(allContacts) ? allContacts : []);
      setTotalContactCount(allContacts?.count ?? contacts.length);

      const map = {};
      contacts.forEach((c) => (map[c.id] = c));
      setContactsMap(map);
    } finally {
      setLoadingInit(false);
    }
  }, [domain, token, requestCompanyId]);

  useEffect(() => {
    fetchInit();
  }, [fetchInit]);

  const fetchMetrics = useCallback(async () => {
    if (!domain || !token || !filters.fromDate || !filters.toDate) return;
    if (metricsAbortRef.current) metricsAbortRef.current.abort();
    const controller = new AbortController();
    metricsAbortRef.current = controller;
    const signal = controller.signal;

    try {
      setLoadingMetrics(true);
      const headers = { Authorization: `token ${token}` };

      const assignedParam =
        filters.userId && filters.userId !== "all"
          ? `&assigned_to=${filters.userId}`
          : "";
      const reminderAssignedParam =
        filters.userId && filters.userId !== "all"
          ? `&reminder_assigned_to=${filters.userId}`
          : "";
      const statusParam =
        filters.statusId !== "all" ? `&status_id=${filters.statusId}` : "";
      const outcomeParam =
        filters.outcome !== "all"
          ? `&outcome=${encodeURIComponent(filters.outcome)}`
          : "";

      const timezoneParam = `&timezone=${browserTimeZone()}`;
      const baseQs = `start_date=${filters.fromDate}&end_date=${filters.toDate}${timezoneParam}${assignedParam}${statusParam}${outcomeParam}`;
      const qs = `${baseQs}&page_size=${METRICS_PAGE_SIZE}`;
      const remQs = `start_date=${filters.fromDate}&end_date=${filters.toDate}${reminderAssignedParam}${statusParam}&page_size=${METRICS_PAGE_SIZE}`;
      const companyQs = `${baseQs}&limit=5`;
      const outboundCountQs = `${baseQs}&direction=outbound&page_size=1`;

      let contactQsParams = "";
      if (filters.userId && filters.userId !== "all") {
        contactQsParams = `&assigned_to=${filters.userId}`;
      } else {
        contactQsParams = "&mine=true";
      }

      const [
        activitiesResult,
        callsResult,
        remindersResult,
        companyStatsResult,
        mineContactsRes,
        outboundCountResult,
      ] = await Promise.allSettled([
        axios.get(`${domain}crm/activities/?${qs}`, { headers, signal }),
        axios.get(`${domain}crm/calls/?${qs}`, { headers, signal }),
        axios.get(`${domain}crm/reminders/?${remQs}`, { headers, signal }),
        axios.get(`${domain}crm/company-stats/?${companyQs}`, {
          headers,
          signal,
        }),
        axios.get(`${domain}crm/contacts/?page_size=1${contactQsParams}`, {
          headers,
          signal,
        }),
        axios.get(`${domain}crm/calls/?${outboundCountQs}`, {
          headers,
          signal,
        }),
      ]);

      if (signal.aborted) return;

      if (mineContactsRes && mineContactsRes.status === "fulfilled") {
        setMineContactCount(mineContactsRes.value.data?.count ?? 0);
      }

      const activities = getSettledList(activitiesResult, false, "activities");
      const rawCalls = getSettledList(callsResult, false, "calls");
      const calls = filterCallsByOutcome(rawCalls, filters.outcome);
      const callsCount = getSettledCount(callsResult, calls.length);
      const outboundCallsCount = getSettledCount(outboundCountResult, 0);
      const reminders = getSettledList(remindersResult, false, "reminders");
      const apiCompanyStats =
        companyStatsResult.status === "fulfilled"
          ? normalizeCompanyStats(companyStatsResult.value.data)
          : [];
      const nextCompanyStats =
        apiCompanyStats.length > 0
          ? apiCompanyStats
          : buildCompanyStatsFromCalls(calls);

      setMetrics({
        activities,
        calls,
        callsCount,
        outboundCallsCount,
        reminders,
      });
      setCompanyStats(nextCompanyStats);
      setLastUpdated(new Date());
    } catch (err) {
      if (err.name === "CanceledError" || err.name === "AbortError") return;
    } finally {
      if (!signal.aborted) setLoadingMetrics(false);
    }
  }, [domain, token, filters]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const fetchAllReminders = useCallback(async () => {
    if (!domain || !token) return;
    try {
      setLoadingAllReminders(true);
      const headers = { Authorization: `token ${token}` };
      const today = new Date();
      const from = format(
        new Date(today.getFullYear(), today.getMonth() - 1, 1),
        "yyyy-MM-dd",
      );
      const to = format(
        new Date(today.getFullYear(), today.getMonth() + 2, 0),
        "yyyy-MM-dd",
      );
      const assignedParam =
        filters.userId && filters.userId !== "all"
          ? `&reminder_assigned_to=${filters.userId}`
          : "";

      const res = await axios.get(
        `${domain}crm/reminders/?start_date=${from}&end_date=${to}&timezone=${browserTimeZone()}${assignedParam}&page_size=200`,
        { headers },
      );
      const data = Array.isArray(res.data)
        ? res.data
        : (res.data?.results ?? []);
      setAllReminders(data);
    } catch {
    } finally {
      setLoadingAllReminders(false);
    }
  }, [domain, token, filters.userId]);

  useEffect(() => {
    fetchAllReminders();
  }, [fetchAllReminders]);

  const handleRefresh = () => {
    fetchMetrics();
    fetchAllReminders();
  };

  const overdueRemindersCount = allReminders.filter(
    (r) => new Date(r.date) < new Date() && r.status !== "Completed",
  ).length;

  const isLoading = loadingInit || loadingMetrics;

  const KpiSkeleton = () => (
    <Skeleton
      variant="rectangular"
      height={150}
      sx={{ borderRadius: "16px" }}
    />
  );

  const contactLabel =
    filters.userId === "all"
      ? "Mine"
      : users.find((u) => String(u.id) === String(filters.userId))
          ?.first_name || "Agent";

  return (
    <Box sx={{ background: COLORS.pageBg, minHeight: "100vh", p: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2.5}
      >
        <Typography variant="h4" fontWeight={700} color="text.primary">
          Dashboard
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} size="small">
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="caption" color="text.disabled">
            {lastUpdated
              ? `Last updated: ${formatDistanceToNow(lastUpdated, { addSuffix: true })}`
              : "Last updated: —"}
          </Typography>
        </Box>
      </Box>

      <DashFilterBar
        users={users}
        statuses={statuses}
        filters={filters}
        onChange={setFilters}
        loggedInUserId="all"
      />

      <Grid container spacing={3} mb={3} mt={0.5}>
        <Grid item xs={12} sm={6} md={3}>
          {loadingInit ? (
            <KpiSkeleton />
          ) : (
            <AnimatedCard
              color="primary"
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <Box
                p={2.5}
                py={2}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  height: "100%",
                }}
              >
                <Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    mb={0.5}
                  >
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      color="text.secondary"
                      sx={{ fontSize: "0.95rem" }}
                    >
                      Total Contacts
                    </Typography>
                    <IconWrapper color="primary">
                      <People sx={{ fontSize: 24 }} />
                    </IconWrapper>
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "2.125rem",
                      lineHeight: 1.1,
                      color: COLORS.indigo,
                    }}
                  >
                    {mineContactCount.toLocaleString()}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      sx={{ color: COLORS.indigo, fontSize: "0.75rem" }}
                    >
                      {contactLabel}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ fontSize: "0.75rem" }}
                    >
                      · {totalContactCount.toLocaleString()} All
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  variant="caption"
                  component="a"
                  onClick={() => history.push("/admin/crm/contacts")}
                  sx={{
                    cursor: "pointer",
                    color: COLORS.indigo,
                    mt: 1,
                    display: "block",
                    fontWeight: 600,
                  }}
                >
                  View all contacts →
                </Typography>
              </Box>
            </AnimatedCard>
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          {isLoading ? (
            <KpiSkeleton />
          ) : (
            <MetricCard
              title="Total Activities"
              value={metrics?.activities?.length ?? 0}
              icon={Timeline}
              color="success"
              onClick={() => {
                setActiveKpi(0);
                setActiveTab(0);
                document
                  .getElementById("upcoming-activities-panel")
                  ?.scrollIntoView({ behavior: "smooth", block: "center" });
              }}
              isActive={activeKpi === 0}
            />
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          {isLoading ? (
            <KpiSkeleton />
          ) : (
            <MetricCard
              title="Calls Made"
              value={metrics?.outboundCallsCount ?? 0}
              icon={Phone}
              color="warning"
              onClick={() => {
                setActiveKpi(1);
                document
                  .getElementById("call-logs-panel")
                  ?.scrollIntoView({ behavior: "smooth", block: "center" });
              }}
              isActive={activeKpi === 1}
            />
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          {isLoading ? (
            <KpiSkeleton />
          ) : (
            <MetricCard
              title="Overdue Reminders"
              value={overdueRemindersCount}
              icon={Notifications}
              color="error"
              onClick={() => {
                setActiveKpi(2);
                setRemindersTab(0);
                document
                  .getElementById("reminders-panel")
                  ?.scrollIntoView({ behavior: "smooth", block: "center" });
              }}
              isActive={activeKpi === 2}
            />
          )}
        </Grid>
      </Grid>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} lg={4} id="upcoming-activities-panel">
          <UpcomingActivitiesPanel
            activities={metrics?.activities ?? []}
            contactsMap={contactsMap}
            isLoading={isLoading}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            isActive={activeKpi === 0}
          />
        </Grid>
        <Grid item xs={12} lg={4} id="call-logs-panel">
          <CallLogsPanel
            calls={metrics?.calls ?? []}
            totalCount={metrics?.callsCount ?? metrics?.calls?.length ?? 0}
            isLoading={isLoading}
            selectedFromDate={filters.fromDate}
            selectedToDate={filters.toDate}
            selectedAgent={
              filters.userId && filters.userId !== "all" ? filters.userId : null
            }
            statusId={filters.statusId !== "all" ? filters.statusId : null}
            outcome={filters.outcome !== "all" ? filters.outcome : null}
            contactsMap={contactsMap}
            users={users}
            isActive={activeKpi === 1}
          />
        </Grid>
        <Grid item xs={12} lg={4} id="reminders-panel">
          <RemindersPanel
            reminders={allReminders}
            isLoading={loadingAllReminders}
            activeTab={remindersTab}
            onTabChange={setRemindersTab}
            isActive={activeKpi === 2}
            contactsMap={contactsMap}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={5}>
          <ActivityOverviewChart
            activities={metrics?.activities ?? []}
            calls={metrics?.calls ?? []}
            fromDate={filters.fromDate}
            toDate={filters.toDate}
            isLoading={isLoading}
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <TopCompaniesPanel stats={companyStats} isLoading={isLoading} />
        </Grid>
        <Grid item xs={12} lg={3}>
          <QuickActionsPanel />
        </Grid>
      </Grid>
    </Box>
  );
}
